/**
 * config.js
 * Zentrale Konfigurationsdatei für die Anwendung.
 * Verwendet die environmentConfig.js für umgebungsspezifische Werte.
 */

import {
  AppConfig,
  API_URL as ENV_CONFIG_API_URL, // Umbenannt, um Kollision mit lokalem API_URL zu vermeiden
  RESTAURANT_SLUG as ENV_CONFIG_RESTAURANT_SLUG,
  CURRENT_ROLE as ENV_CONFIG_CURRENT_ROLE,
  IS_PRODUCTION,
  IS_DEVELOPMENT
} from './utils/environmentConfig';

// Konstantendefinitionen für Default-Werte
const DEFAULT_API_URL = 'https://api.astra-it.at'; // Fallback, falls ENV_CONFIG_API_URL leer ist
const DEFAULT_APP_NAME = 'Table Order';
const DEFAULT_COLOR = '#3490dc';
const DEFAULT_DOMAIN = 'astra-it.at'; // Wird von AppConfig.frontendUrl abgeleitet wenn möglich

// API-Konfiguration mit Fallback
export const API_URL = ENV_CONFIG_API_URL || DEFAULT_API_URL;
console.log(`[config] API_URL = ${API_URL}`);

// Basis-URL (ohne /api) für statische Ressourcen
// In Prod: z.B. https://api.ensu.astra-it.at -> https://ensu.astra-it.at (wenn API und Frontend auf Subdomains des Restaurants liegen)
// In Dev: ENV_CONFIG_API_URL ist /api, AppConfig.frontendUrl ist http://localhost:8080
export const BASE_URL = (() => {
  if (IS_DEVELOPMENT) {
    return AppConfig.frontendUrl; // z.B. http://localhost:8080
  }
  // In Produktion, versuche von der API-URL abzuleiten oder nimm die Frontend-URL
  if (AppConfig.frontendUrl) {
    return AppConfig.frontendUrl.endsWith('/') ? AppConfig.frontendUrl.slice(0, -1) : AppConfig.frontendUrl;
  }
  // Fallback: versuche von API_URL abzuleiten (api.slug.domain -> slug.domain)
  try {
    const url = new URL(API_URL);
    if (url.hostname.startsWith('api.')) {
      return `${url.protocol}//${url.hostname.substring(4)}${url.port ? ':' + url.port : ''}`;
    }
    return `${url.protocol}//${url.hostname}${url.port ? ':' + url.port : ''}`;
  } catch (e) {
    return ''; // Letzter Fallback
  }
})();
console.log(`[config] BASE_URL (für statische Assets relativ zum Frontend Host) = ${BASE_URL}`);

// Stripe Konfiguration
// Der Benutzer muss STRIPE_PUBLISHABLE_KEY in .env (oder .env.production) bereitstellen
export const STRIPE_PUBLISHABLE_KEY = process.env.STRIPE_PUBLISHABLE_KEY || '';
if (!STRIPE_PUBLISHABLE_KEY && IS_PRODUCTION) {
  console.warn('[config] Stripe Publishable Key (STRIPE_PUBLISHABLE_KEY) ist nicht in der Umgebung gesetzt für eine Produktionsumgebung.');
}

// Anwendungsspezifische Konfiguration
// VUE_APP_NAME wäre der übliche Weg mit Vue CLI für .env Variablen
export const APP_NAME = process.env.VUE_APP_NAME || DEFAULT_APP_NAME;
export const COPYRIGHT_YEAR = new Date().getFullYear();

// Restaurant-spezifische Konfiguration mit Validierung und Fallbacks
export const RESTAURANT_SLUG = ENV_CONFIG_RESTAURANT_SLUG; // kommt direkt aus environmentConfig
export const RESTAURANT_NAME = AppConfig.restaurantName || RESTAURANT_SLUG; // restaurantName ist in AppConfig nicht definiert, Slug als Fallback
export const PRIMARY_COLOR = AppConfig.primaryColor || DEFAULT_COLOR; // primaryColor ist in AppConfig nicht definiert

if (IS_PRODUCTION && !RESTAURANT_SLUG) {
  console.error('[config] Restaurant-Slug ist nicht gesetzt in Produktionsumgebung!');
}

// Rollen-Konfiguration
export const CURRENT_ROLE = ENV_CONFIG_CURRENT_ROLE;

// Entwicklungsumgebung (direkt von environmentConfig)
export const IS_DEV_ENVIRONMENT = IS_DEVELOPMENT;

// Restaurant-Features (Funktionen, die ein Restaurant aktiviert hat)
// FEATURES ist in AppConfig nicht definiert. Muss anderweitig geladen werden, z.B. aus RestaurantSettings
export const FEATURES = AppConfig.features || {};

// Basis-Domain für Produktionsumgebung
// PROD_BASE_DOMAIN ist in AppConfig nicht direkt exportiert, aber Teil der URL-Konstruktion.
// Wir können versuchen, es aus AppConfig.frontendUrl abzuleiten, falls benötigt.
const extractBaseDomain = (url) => {
  try {
    const hostname = new URL(url).hostname;
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return parts.slice(-2).join('.'); // z.B. astra-it.at
    }
  } catch (e) { /* ignore */ }
  return DEFAULT_DOMAIN;
};
export const BASE_DOMAIN = IS_PRODUCTION ? extractBaseDomain(AppConfig.frontendUrl) : DEFAULT_DOMAIN;


export const getImageUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;

  const backendBase = IS_DEVELOPMENT
    ? 'http://localhost:8000'
    : (API_URL.startsWith('/') ? '' : new URL(API_URL).origin);

  if (path.startsWith('/uploads/') || path.startsWith('/restaurant-logos/')) {
    return `${backendBase}/storage${path}`;
  }
  if (path.startsWith('restaurants/') || path.startsWith('uploads/')) {
    return `${backendBase}/storage/${path}`;
  }
  if (!path.includes('/')) {
    return `${backendBase}/storage/uploads/products/${encodeURIComponent(path)}`;
  }
  return path;
};

export const getPublicUrl = (slug = null) => {
  if (IS_DEVELOPMENT) {
    // Im Dev Modus ist AppConfig.frontendUrl (http://localhost:8080) die korrekte Basis.
    // Wenn ein anderer Slug zum Testen verwendet werden soll, muss dies per URL-Parameter an environmentConfig übergeben werden (nicht implementiert)
    return AppConfig.frontendUrl;
  }
  // Produktion: AppConfig.frontendUrl ist bereits https://[slug].astra-it.at
  // Wenn ein spezifischer Slug übergeben wird, der vom aktuellen abweicht, neu bauen.
  if (slug && slug !== AppConfig.restaurantSlug) {
    return `https://kiosk.${slug}.${BASE_DOMAIN}`;
  }
  // Für die Standard-URL, füge 'kiosk' vor dem Restaurant-Slug hinzu
  const restaurantSlug = AppConfig.restaurantSlug || RESTAURANT_SLUG;
  if (restaurantSlug) {
    return `https://kiosk.${restaurantSlug}.${BASE_DOMAIN}`;
  }
  return AppConfig.frontendUrl;
};

export const getEnvironment = () => {
  if (IS_DEVELOPMENT) return 'development';
  // Staging Erkennung - ggf. anpassen, falls staging Subdomains verwendet werden, z.B. staging.ensu.astra-it.at
  if (AppConfig.frontendUrl.includes('staging') || AppConfig.frontendUrl.includes('test') || AppConfig.frontendUrl.includes('dev.')) {
    return 'staging';
  }
  return 'production';
};

console.log('[config] Umgebung:', getEnvironment());
console.log('[config] Aktuelle Rolle:', CURRENT_ROLE);
console.log('[config] Restaurant-Slug:', RESTAURANT_SLUG);
console.log('[config] Restaurant-Features:', FEATURES); 